import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { EntityDeploymentState } from './deploymentState';

/**
 * Deployment state for every component on a releases view, in one request.
 *
 * The overview lists 200 releases, which is a few dozen components; asking per
 * row would be dozens of round trips from the browser and hundreds of cluster
 * reads. The backend batches it (`GET /api/deployments/entities/state`) and
 * reads only the ArgoCD Application and the Deployments per entity.
 *
 * Failure is quiet on purpose. Deployment state is an enrichment of a page that
 * was useful before it existed, so a cluster that cannot be reached, or an
 * instance with none configured (501), leaves the releases listed and the chips
 * absent rather than replacing the page with an error.
 */
export function useDeploymentStates(
  entityRefs: string[],
  options: {
    /**
     * Bump to refetch. Views that poll pass a counter; the deployment state is
     * live data, so a long-lived tab would otherwise keep showing whatever was
     * true when it was opened.
     */
    nonce?: number;
  } = {},
): {
  states: Record<string, EntityDeploymentState>;
  loading: boolean;
} {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  // Deduplicated, sorted and joined into one string, which is the actual
  // dependency: the caller's array is rebuilt on every render and reordered
  // whenever the table is sorted, and depending on it directly would refetch on
  // both. The refs are read back out of the key inside the request, so nothing
  // else has to be a dependency.
  const key = [...new Set(entityRefs)].sort().join(' ');

  const { value, loading } = useAsync(async (): Promise<
    Record<string, EntityDeploymentState>
  > => {
    const refs = key ? key.split(' ') : [];
    if (refs.length === 0) {
      return {};
    }
    const baseUrl = await discoveryApi.getBaseUrl('deployments');
    const query = refs.map(ref => `ref=${encodeURIComponent(ref)}`).join('&');
    const response = await fetch(`${baseUrl}/entities/state?${query}`);
    if (!response.ok) {
      return {};
    }
    return ((await response.json()).states ?? {}) as Record<
      string,
      EntityDeploymentState
    >;
  }, [key, options.nonce, discoveryApi, fetch]);

  return { states: value ?? {}, loading };
}
